import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/lib/site";

const NAV = [
  { label: "Home", to: "/" },
  { label: "For Businesses", to: "/#businesses" },
  { label: "Materials", to: "/#materials" },
  { label: "How It Works", to: "/#how-it-works" },
  { label: "Partners", to: "/partners" },
  { label: "Compliance", to: "/compliance" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-dc-bg/85 backdrop-blur-md border-b border-dc-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <button data-testid="nav-logo" onClick={() => handleNav("/")} className="flex flex-col items-start leading-none group">
          <span className="font-display text-lg lg:text-xl font-semibold tracking-tight text-white">
            DES-CARTA<span className="text-dc-accent"> LTD</span>
          </span>
          <span className="hidden sm:block text-[9px] tracking-[0.25em] uppercase text-dc-text2 mt-1 font-mono">
            Recovered Materials Brokerage
          </span>
        </button>

        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((n) => (
            <button
              key={n.label}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => handleNav(n.to)}
              className="text-[13px] tracking-wide text-dc-text2 hover:text-white transition-colors duration-200"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-assessment"
            onClick={() => handleNav("/contact")}
            className="hidden sm:inline-flex items-center gap-1.5 bg-dc-accent text-dc-bg text-[13px] font-semibold px-4 py-2.5 hover:bg-dc-accentHover transition-colors duration-200"
          >
            Request Assessment <ArrowUpRight size={15} />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="xl:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="xl:hidden bg-dc-bg border-t border-dc-border">
          <div className="px-4 py-4 flex flex-col">
            {NAV.map((n) => (
              <button
                key={n.label}
                onClick={() => handleNav(n.to)}
                className="text-left py-3 text-sm text-dc-text2 hover:text-dc-accent border-b border-dc-border/60 transition-colors"
              >
                {n.label}
              </button>
            ))}
            <button
              onClick={() => handleNav("/contact")}
              className="mt-4 bg-dc-accent text-dc-bg font-semibold px-4 py-3 text-sm"
            >
              Request a Waste Value Assessment
            </button>
            <a href={BRAND.phoneHref} className="mt-3 text-center text-dc-accent font-mono text-sm py-2">
              {BRAND.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
